import { apiClient } from '../config/api';

// FEAT-063 admin client (MVP-18.6, MVP-18.25).
// Typed wrappers for service-area and region management endpoints.

export interface RegionRef {
  id: string;
  name: string;
  active: boolean;
}

export interface ServiceAreaAdminView {
  id: string;
  zip: string;
  city: string | null;
  state: string | null;
  active: boolean;
  regionId: string | null;
  region: RegionRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegionServiceAreaItem {
  id: string;
  zip: string;
  city: string | null;
  state: string | null;
  active: boolean;
}

export interface RegionAdminView {
  id: string;
  name: string;
  active: boolean;
  serviceAreas: RegionServiceAreaItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceAreaInput {
  zip: string;
  city?: string;
  state?: string;
  active?: boolean;
  regionId?: string | null;
}

export interface PatchServiceAreaInput {
  active?: boolean;
  city?: string;
  state?: string;
  regionId?: string | null;
}

export interface CreateRegionInput {
  name: string;
  active?: boolean;
}

export interface PatchRegionInput {
  name?: string;
  active?: boolean;
}

class ServiceAreasAdminService {
  async listServiceAreas(params?: { active?: boolean; regionId?: string }): Promise<ServiceAreaAdminView[]> {
    const query = new URLSearchParams();
    if (params?.active !== undefined) query.set('active', String(params.active));
    if (params?.regionId) query.set('regionId', params.regionId);
    const qs = query.toString();
    const response = await apiClient.get<{ serviceAreas: ServiceAreaAdminView[] }>(
      `/admin/service-areas${qs ? `?${qs}` : ''}`,
    );
    return response.data.serviceAreas ?? [];
  }

  async createServiceArea(input: CreateServiceAreaInput): Promise<ServiceAreaAdminView> {
    const response = await apiClient.post<{ serviceArea: ServiceAreaAdminView }>(
      '/admin/service-areas',
      input,
    );
    return response.data.serviceArea;
  }

  async patchServiceArea(id: string, input: PatchServiceAreaInput): Promise<ServiceAreaAdminView> {
    const response = await apiClient.patch<{ serviceArea: ServiceAreaAdminView }>(
      `/admin/service-areas/${encodeURIComponent(id)}`,
      input,
    );
    return response.data.serviceArea;
  }

  async deleteServiceArea(id: string): Promise<void> {
    await apiClient.delete<{ deleted: boolean; id: string }>(
      `/admin/service-areas/${encodeURIComponent(id)}`,
    );
  }

  async listRegions(): Promise<RegionAdminView[]> {
    const response = await apiClient.get<{ regions: RegionAdminView[] }>('/admin/regions');
    return response.data.regions ?? [];
  }

  async createRegion(input: CreateRegionInput): Promise<RegionAdminView> {
    const response = await apiClient.post<{ region: RegionAdminView }>('/admin/regions', input);
    return response.data.region;
  }

  async patchRegion(id: string, input: PatchRegionInput): Promise<RegionAdminView> {
    const response = await apiClient.patch<{ region: RegionAdminView }>(
      `/admin/regions/${encodeURIComponent(id)}`,
      input,
    );
    return response.data.region;
  }

  async assignServiceAreasToRegion(
    regionId: string,
    serviceAreaIds: string[],
  ): Promise<ServiceAreaAdminView[]> {
    const response = await apiClient.post<{ assigned: number; serviceAreas: ServiceAreaAdminView[] }>(
      `/admin/regions/${encodeURIComponent(regionId)}/service-areas`,
      { serviceAreaIds },
    );
    return response.data.serviceAreas ?? [];
  }
}

export const serviceAreasAdminService = new ServiceAreasAdminService();
